import type { FormLookup } from "../types.js";
import "./styles.css";

export const LOOKUP_LABEL = "Look up";
export const LOOKUP_BUSY_LABEL = "Checking";

/**
 * Phase 561 — what a CONFIRMED object says about itself: the found-record card and the candidate
 * the subject picked. A generic default, like every other lookup label here.
 */
export const LOOKUP_CONFIRMED_LABEL = "Confirmed";

export type LookupTone = "success" | "notice";

export interface LookupStatus {
  tone: LookupTone;
  message: string;
}

export interface LookupActionProps {
  lookup: FormLookup;
  ready: boolean;
  busy: boolean;
  status: LookupStatus | null;
  onRun: () => void;
  /** Show the success sentence too (a lookup with no `show` card has nothing else to say). */
  announceSuccess?: boolean;
}

/**
 * The "Look up" control that sits beside its trigger field; the result card — or, since Phase 524,
 * the candidate list — renders below.
 *
 * <p>The label comes from the declaration when it wrote one (`action_label`), because a step may
 * carry two lookups over the same record: one that FINDS a candidate and one that VERIFIES the
 * chosen identifier. Absent, it is {@link LOOKUP_LABEL} exactly as before, so every lookup declared
 * before that key renders byte-identically.</p>
 *
 * <p>The status line below the button is the seam every degraded arm renders through — including
 * the rate limit, which is REACHABLE on the happy path now that a form can search, refine, search
 * again and then verify inside one minute. It is deliberately a `notice`, never an `alert` and
 * never an automatic retry: the subject is told what happened and what to do, the button stays
 * live, and every answer the lookup would have filled can still be typed by hand.</p>
 */
export function LookupAction({ lookup, ready, busy, status, onRun, announceSuccess = false }: LookupActionProps) {
  const statusId = `${lookup.id}-lookup-status`;
  const label = lookup.action_label?.trim() ? lookup.action_label.trim() : LOOKUP_LABEL;
  return (
    <div className="lookup-action">
      <button
        type="button"
        id={`${lookup.id}-lookup`}
        className="lookup-button"
        disabled={busy || !ready}
        aria-busy={busy}
        aria-describedby={status ? statusId : undefined}
        onClick={onRun}
      >
        {busy ? <><span className="lookup-spinner" aria-hidden="true" />{LOOKUP_BUSY_LABEL}</> : label}
      </button>
      {status && (status.tone === "notice" || announceSuccess) && (
        <p id={statusId} className={`lookup-status lookup-status-${status.tone}`} role="status" aria-live="polite">
          {status.message}
        </p>
      )}
    </div>
  );
}

/**
 * Phase 561 — THE CONFIRMED MARK: a tick plus {@link LOOKUP_CONFIRMED_LABEL}, in the platform
 * success tint. It is what a tint alone cannot say — that this record is the settled answer, not
 * one more option — and it renders ONLY on a confirmed object (the found-record card, the picked
 * candidate), never on a candidate still waiting to be chosen.
 *
 * <p>The tick is decorative (`aria-hidden`); the label is real text, so a screen-reader user hears
 * "Confirmed" rather than a glyph name. A `span` so it is valid inside the candidate's button.</p>
 */
export function LookupConfirmedMark() {
  return (
    <span className="lookup-confirmed">
      <span className="lookup-confirmed-tick" aria-hidden="true" />
      {LOOKUP_CONFIRMED_LABEL}
    </span>
  );
}

export interface LookupResultCardProps {
  eyebrow: string;
  title: string;
  rows: ReadonlyArray<{ label: string; value: string; mono?: boolean; status?: boolean }>;
}

/**
 * KYC-design — the found-record card: the ONE job of `--brand-accent`. Shows what the lookup
 * found (name, number, status, address) so the client confirms a real thing, not a green
 * sentence. Continue is the confirmation.
 *
 * <p>Phase 561 — it carries the {@link LookupConfirmedMark}: the fill says "done", the mark says
 * "confirmed" in words, and the brand edge stays so the card still reads as the practice's.</p>
 */
export function LookupResultCard({ eyebrow, title, rows }: LookupResultCardProps) {
  return (
    <div className="lookup-result" role="status" aria-live="polite">
      <LookupConfirmedMark />
      <p className="lookup-result-eyebrow">{eyebrow}</p>
      <p className="lookup-result-title">{title}</p>
      {rows.length > 0 && (
        <dl className="lookup-result-grid">
          {rows.map((row) => (
            <div key={row.label} style={{ display: "contents" }}>
              <dt>{row.label}</dt>
              <dd className={row.mono ? "lookup-result-mono" : undefined}>
                {row.status && <span className="status-dot" aria-hidden="true" />}
                {row.value}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}
